import React, { FunctionComponent } from 'react';

import Currency from 'ui/atoms/currency';
import Number from 'ui/atoms/number';
import Tag from 'ui/atoms/tag';
import Translate from 'ui/atoms/translate';
import { RuleType } from 'ui/types/investment';
import { camelCase } from 'change-case';
import { CountryEnum, InvestmentTokenForInvestor, Money } from 'api';
import Date from 'ui/atoms/date';
import CardItem from 'src/ui/atoms/card-item';

export interface ProductInvitationCardProps {
  id: string;
  minNumberOfTokens?: number;
  maxNumberOfTokens?: number;
  pricePerToken: Money;
  country?: CountryEnum;
  email?: string;
  status: 'new' | 'committed';
  ruleType: string;
  /** Additional classes. */
  className?: string;
  token?: InvestmentTokenForInvestor;
  name?: string;
  createdAt?: Date;
  signedPercentage: number;
  distributionPlatform: string;
}

// TODO(geforcefan): remove minimumTicketSizeAlternative translation, params needed for rule types
const ProductInvitationCard: FunctionComponent<ProductInvitationCardProps> = (props) => {
  const {
    id,
    minNumberOfTokens = 0,
    maxNumberOfTokens = 0,
    pricePerToken,
    country,
    email,
    status,
    ruleType,
    name,
    createdAt,
    signedPercentage,
    token,
    distributionPlatform,
  } = props;

  return (
    <>
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.product" truncate={true} />}
        content={token?.name}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.personalInvitationId" truncate={true} />}
        content={id}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.typeOfOffering" truncate={true} />}
        content={
          <Translate
            name={
              ruleType === RuleType.MinimumTicketSize
                ? `ruleType.minimumTicketSizeAlternative`
                : `ruleType.${camelCase(ruleType)}`
            }
          />
        }
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.reference" truncate={true} />}
        content={name}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.createdAt" truncate={true} />}
        content={<Date>{createdAt}</Date>}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.distributionPlatform" truncate={true} />}
        content={distributionPlatform}
      />
      <CardItem
        title={<Translate name="investmentInvitation.pricePerToken" truncate={true} />}
        content={<Currency decimals={'*'}>{pricePerToken}</Currency>}
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.allocation.title" truncate={true} />}
        content={
          <Translate
            name="issuerProductDetails.campaigns.campaignDetails.allocation.content"
            args={[(_, key) => <Number key={key}>{maxNumberOfTokens}</Number>]}
          />
        }
      />
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.investmentsRange" truncate={true} />}
        content={
          <Translate
            name="studioCampaignInvestmentCard.nrOfTokenRange.content"
            args={[
              (_, key) => <Number key={key}>{minNumberOfTokens}</Number>,
              (_, key) => <Number key={key}>{maxNumberOfTokens}</Number>,
            ]}
          />
        }
      />
      {email && <CardItem title={<Translate name="common.email" />} content={email} />}
      <CardItem
        title={<Translate name="issuerProductDetails.campaigns.campaignDetails.signed" truncate={true} />}
        content={`${signedPercentage.toFixed()}%`}
      />
      {country && (
        <CardItem
          title={<Translate name="issuerProductDetails.campaigns.campaignDetails.market" truncate={true} />}
          content={<Translate name={`countries.${country}`} />}
        />
      )}
      <CardItem
        title={<Translate name="common.status" truncate={true} />}
        content={
          <Tag color="info" truncate={true} padded="tiny">
            <Translate name={`investmentInvitation.status.${status}`} />
          </Tag>
        }
      />
    </>
  );
};

export default ProductInvitationCard;
